// ScrollX — the wrapper for every table that scrolls sideways (Philip 2026-10-27): a second horizontal scrollbar ON TOP
// of the table, kept in step with the bottom one, so nobody has to scroll to the end of a long list to move sideways.
// The top bar shows only while the table actually overflows; a table that fits looks exactly as before.
//
// It is also the scrolling ancestor the pinned columns stick to (pinCell, below): position: sticky works against the
// nearest scrolling box, which is this one.
//
// ⛔ overflowX: auto makes overflowY auto too (CSS has no visible/auto mix), so anything absolutely positioned inside
// the table — a header filter popover — is clipped by this box. Such a popover renders through a portal instead.
import React, { useLayoutEffect, useRef, useState } from 'react';

export default function ScrollX({ children, style, topStyle }) {
  const bottom = useRef(null);
  const top = useRef(null);
  const syncing = useRef(false);
  const [size, setSize] = useState({ width: 0, over: false });

  // Re-measured after every render (a column switched on, rows filtered) and whenever the box or the table resizes.
  useLayoutEffect(() => {
    const el = bottom.current;
    if (!el) return undefined;
    const measure = () => {
      const width = el.scrollWidth, over = el.scrollWidth > el.clientWidth + 1;
      setSize((prev) => (prev.width === width && prev.over === over ? prev : { width, over }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  });

  // One bar follows the other; the flag stops the echo (setting scrollLeft fires the other bar's scroll event).
  const follow = (from, to) => () => {
    if (syncing.current) { syncing.current = false; return; }
    if (!from.current || !to.current || to.current.scrollLeft === from.current.scrollLeft) return;
    syncing.current = true;
    to.current.scrollLeft = from.current.scrollLeft;
  };

  return (
    <>
      {size.over && (
        <div ref={top} onScroll={follow(top, bottom)} aria-hidden="true"
          style={{ overflowX: 'auto', overflowY: 'hidden', height: '14px', ...topStyle }}>
          <div style={{ width: `${size.width}px`, height: '1px' }} />
        </div>
      )}
      <div ref={bottom} onScroll={follow(bottom, top)} style={{ overflowX: 'auto', ...style }}>
        {children}
      </div>
    </>
  );
}

// A pinned (sticky) cell: it stays at `left` px while the rest of the row scrolls under it. Pinned cells need an
// OPAQUE background or the scrolled columns show through; the last pinned column carries a divider so the edge reads.
// Header cells sit above body cells (zIndex) so a pinned header never disappears under a pinned body cell.
export const pinCell = (left, { background = 'var(--panel)', last = false, header = false } = {}) => ({
  position: 'sticky', left: `${left}px`, zIndex: header ? 4 : 2, background,
  ...(last ? { boxShadow: 'inset -1px 0 0 var(--line)' } : {}),
});

// ⛔ TWO GUARDS FOR A TABLE WITH PINNED CELLS (Philip 2026-10-27). The first build showed STALE ROWS on the live
// Executive Summary — Palm Springs rows, one of them twice, under an NY filter whose summary was right — while the
// rows React rendered were right (no duplicate keys, a test replaces the rows on a filter change). That is the browser
// failing to repaint, and the pinned (sticky) cells were the one new thing in the drawing.
//  1. PINNED_TABLE: separate borders, not collapsed. Chrome's sticky table cells are known to paint wrongly in a
//     border-collapse table; with border-spacing 0 the separate model draws the same lines.
//  2. redrawKey(ids): the table BODY's key, from its rows' ids — a different set or order of rows draws a fresh body,
//     so no pixel of the old rows can survive a filter change. The header is kept (a filter popover typed into must not
//     remount per keystroke), and so is the scroll box's position.
export const PINNED_TABLE = { borderCollapse: 'separate', borderSpacing: 0 };

export function redrawKey(ids) {
  let h = 5381;
  ids.forEach((id) => { const s = String(id); for (let i = 0; i < s.length; i += 1) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0; h = ((h * 33) ^ 124) >>> 0; });
  return `${ids.length}:${h.toString(36)}`;
}
